import {
  playNotificationSound,
  prepareNotificationAudio,
  startCallRingtone,
  stopCallRingtone as stopLocalCallRingtone,
  type NotificationSoundId,
} from "@/lib/notification-sounds";

const CHANNEL_NAME = "airie-audio-notifications";
const LEADER_KEY = "airie:audio:leader";
const REQUEST_KEY = "airie:audio:last-request";
const HEARTBEAT_INTERVAL_MS = 2_000;
const LEADER_STALE_MS = 6_000;
const DEDUPE_TTL_MS = 5_000;
const NOTIFICATION_RATE_LIMIT_MS = 1_500;

type AudioMessage =
  | {
      type: "notification";
      id: string;
      sound: NotificationSoundId;
      volume: number;
      sourceTabId: string;
      sentAt: number;
    }
  | {
      type: "call:start";
      id: string;
      volume: number;
      sourceTabId: string;
      sentAt: number;
    }
  | {
      type: "call:stop";
      id: string;
      sourceTabId: string;
      sentAt: number;
    }
  | {
      type: "unlock";
      sourceTabId: string;
      sentAt: number;
    };

type LeaderRecord = {
  tabId: string;
  heartbeatAt: number;
  visible: boolean;
};

type NotificationSoundRequest = {
  id: string;
  sound: NotificationSoundId;
  volume: number;
};

type CallRingtoneRequest = {
  id: string | number;
  volume: number;
};

const tabId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
let initialized = false;
let broadcastChannel: BroadcastChannel | null = null;
let heartbeatTimer: number | null = null;
let lastNotificationAt = 0;
const recentlyHandled = new Map<string, number>();

function now(): number {
  return Date.now();
}

function pruneHandled(): void {
  const cutoff = now() - DEDUPE_TTL_MS;
  for (const [id, handledAt] of recentlyHandled) {
    if (handledAt < cutoff) {
      recentlyHandled.delete(id);
    }
  }
}

function markHandled(id: string): boolean {
  pruneHandled();
  if (recentlyHandled.has(id)) {
    return false;
  }
  recentlyHandled.set(id, now());
  return true;
}

function readLeader(): LeaderRecord | null {
  try {
    const value = window.localStorage.getItem(LEADER_KEY);
    if (!value) {
      return null;
    }
    const parsed = JSON.parse(value) as Partial<LeaderRecord>;
    if (typeof parsed.tabId !== "string" || typeof parsed.heartbeatAt !== "number") {
      return null;
    }
    return {
      tabId: parsed.tabId,
      heartbeatAt: parsed.heartbeatAt,
      visible: parsed.visible === true,
    };
  } catch {
    return null;
  }
}

function writeLeader(): void {
  try {
    window.localStorage.setItem(
      LEADER_KEY,
      JSON.stringify({
        tabId,
        heartbeatAt: now(),
        visible: document.visibilityState === "visible",
      } satisfies LeaderRecord),
    );
  } catch {
    // localStorage can be unavailable in restrictive browser modes.
  }
}

function isLeaderRecordFresh(leader: LeaderRecord | null): boolean {
  return !!leader && now() - leader.heartbeatAt < LEADER_STALE_MS;
}

function ensureLeadership(): boolean {
  const leader = readLeader();
  const currentTabIsVisible = document.visibilityState === "visible";
  const visibleTabCanTakeOver = currentTabIsVisible && leader?.visible === false;

  if (!isLeaderRecordFresh(leader) || leader?.tabId === tabId || visibleTabCanTakeOver) {
    writeLeader();
    return true;
  }

  return false;
}

function isLeader(): boolean {
  const leader = readLeader();
  return leader?.tabId === tabId && isLeaderRecordFresh(leader);
}

function publish(message: AudioMessage): void {
  broadcastChannel?.postMessage(message);
  try {
    window.localStorage.setItem(REQUEST_KEY, JSON.stringify(message));
  } catch {
    // Best-effort cross-tab fallback only.
  }
}

function playNotificationOnce(message: Extract<AudioMessage, { type: "notification" }>): void {
  if (!markHandled(`notification:${message.id}`)) {
    return;
  }

  const elapsed = now() - lastNotificationAt;
  if (elapsed < NOTIFICATION_RATE_LIMIT_MS) {
    return;
  }

  lastNotificationAt = now();
  void playNotificationSound(message.sound, message.volume).catch((error) => {
    console.warn("Failed to play audio notification:", error);
  });
}

function handleMessage(message: AudioMessage): void {
  if (message.sourceTabId === tabId && message.type !== "unlock") {
    return;
  }

  ensureLeadership();

  if (message.type === "unlock") {
    void prepareNotificationAudio();
    return;
  }

  if (!isLeader()) {
    return;
  }

  if (message.type === "notification") {
    playNotificationOnce(message);
    return;
  }

  if (message.type === "call:start") {
    startCallRingtone(message.id, message.volume);
    return;
  }

  stopLocalCallRingtone(message.id);
}

function handleStorage(event: StorageEvent): void {
  if (event.key === LEADER_KEY && event.newValue) {
    const leader = readLeader();
    if (leader?.tabId !== tabId) {
      return;
    }
  }

  if (event.key !== REQUEST_KEY || !event.newValue) {
    return;
  }

  try {
    handleMessage(JSON.parse(event.newValue) as AudioMessage);
  } catch {
    // Ignore malformed cross-tab payloads.
  }
}

function init(): void {
  if (initialized || typeof window === "undefined") {
    return;
  }

  initialized = true;
  if ("BroadcastChannel" in window) {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
    broadcastChannel.onmessage = (event: MessageEvent<AudioMessage>) => handleMessage(event.data);
  }

  window.addEventListener("storage", handleStorage);
  ensureLeadership();
  heartbeatTimer = window.setInterval(() => {
    ensureLeadership();
  }, HEARTBEAT_INTERVAL_MS);
}

export function installAudioUnlockListeners(): () => void {
  init();

  const unlock = () => {
    void prepareNotificationAudio();
    publish({ type: "unlock", sourceTabId: tabId, sentAt: now() });
  };

  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  window.addEventListener("touchstart", unlock, { once: true });

  return () => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };
}

export function requestNotificationSound(request: NotificationSoundRequest): void {
  init();
  const message: AudioMessage = {
    type: "notification",
    id: request.id,
    sound: request.sound,
    volume: request.volume,
    sourceTabId: tabId,
    sentAt: now(),
  };

  publish(message);
  ensureLeadership();
  if (isLeader()) {
    playNotificationOnce(message);
  }
}

export function requestStartCallRingtone(request: CallRingtoneRequest): void {
  init();
  const message: AudioMessage = {
    type: "call:start",
    id: String(request.id),
    volume: request.volume,
    sourceTabId: tabId,
    sentAt: now(),
  };

  publish(message);
  ensureLeadership();
  if (isLeader()) {
    startCallRingtone(message.id, message.volume);
  }
}

export function requestStopCallRingtone(id: string | number): void {
  init();
  const message: AudioMessage = {
    type: "call:stop",
    id: String(id),
    sourceTabId: tabId,
    sentAt: now(),
  };

  publish(message);
  stopLocalCallRingtone(message.id);
}

export function disposeAudioNotificationCoordinator(): void {
  if (heartbeatTimer !== null) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  broadcastChannel?.close();
  broadcastChannel = null;
  initialized = false;
}
